import { Entity } from './common';
import { UserGender } from './user';

/**
 * ─── Módulo de Tecnificación ──────────────────────────────────────────────────
 *
 * Programas formativo-técnicos por temporada para jugadores seleccionados
 * manualmente por la federación. Disponible solo para tenants `federation`
 * con el módulo `technification` activo. Ver `.docs/technification.md`.
 *
 * Roles secundarios (en `additionalRoles` de los custom claims, NO en `UserRole`):
 *  - TECHNIFICATION_MANAGER → gestiona programas, participantes y técnicos.
 *  - TECHNIFICATION_COACH   → registra sesiones, asistencia y evaluaciones.
 */

/** Nombres canónicos de los roles secundarios de tecnificación. */
export const TECHNIFICATION_MANAGER = 'TECHNIFICATION_MANAGER';
export const TECHNIFICATION_COACH = 'TECHNIFICATION_COACH';
export type TechnificationRole =
  | typeof TECHNIFICATION_MANAGER
  | typeof TECHNIFICATION_COACH;

/** Género objetivo de un programa (extiende UserGender con MIXED). */
export type ProgramGender = UserGender | 'MIXED';

// ─── Programa ────────────────────────────────────────────────────────────────

export interface TechnificationProgram extends Entity {
  name: string; // Ej. "Tecnificación Provincial Alevín 2025"
  seasonId: string;
  ageCategory: string; // Ej. "ALEVIN", "INFANTIL", "CADETE"
  gender?: ProgramGender;
  description?: string;
  /** ISO date string 'YYYY-MM-DD' */
  startDate: string;
  /** ISO date string 'YYYY-MM-DD' */
  endDate: string;
  maxParticipants?: number;
  location?: string;
  participantIds: string[]; // userIds — asignados manualmente por temporada
  coachIds: string[]; // userIds con rol TECHNIFICATION_COACH asignados
  managerIds: string[]; // userIds con rol TECHNIFICATION_MANAGER responsables
  clubIds?: string[]; // Clubs de procedencia (informativo)
  /**
   * Rúbrica de evaluación configurable por programa. Si está vacía/undefined,
   * la UI usa los criterios por defecto (`DEFAULT_RUBRIC`).
   */
  rubric?: RubricCriterion[];
  active: boolean;
}

/** Un criterio puntuable de la rúbrica de un programa (escala 1-`max`). */
export interface RubricCriterion {
  /** Identificador estable, usado como clave en `Evaluation.scores`. */
  key: string;
  label: string;
  /** Puntuación máxima (por defecto 10). */
  max: number;
  /** Peso relativo para la media ponderada (por defecto 1). */
  weight?: number;
  description?: string;
}

/** Rúbrica por defecto cuando el programa no define una propia. */
export const DEFAULT_RUBRIC: RubricCriterion[] = [
  { key: 'technique', label: 'Técnica', max: 10, weight: 1 },
  { key: 'tactics', label: 'Táctica', max: 10, weight: 1 },
  { key: 'physical', label: 'Físico', max: 10, weight: 1 },
  { key: 'attitude', label: 'Actitud', max: 10, weight: 1 },
  { key: 'mentality', label: 'Mentalidad', max: 10, weight: 1 },
];

// ─── Sesión ──────────────────────────────────────────────────────────────────

export enum TechnificationExercise {
  TECHNIQUE = 'TECHNIQUE',
  TACTICS = 'TACTICS',
  PHYSICAL_SPEED = 'PHYSICAL_SPEED',
  PHYSICAL_ENDURANCE = 'PHYSICAL_ENDURANCE',
  PHYSICAL_STRENGTH = 'PHYSICAL_STRENGTH',
  PHYSICAL_FLEXIBILITY = 'PHYSICAL_FLEXIBILITY',
  PHYSICAL_COORDINATION = 'PHYSICAL_COORDINATION',
  SCRIMMAGE = 'SCRIMMAGE',
  VIDEO_ANALYSIS = 'VIDEO_ANALYSIS',
  MENTAL_TRAINING = 'MENTAL_TRAINING',
}

export interface SessionAttendance {
  userId: string;
  present: boolean;
  cause?: string; // Motivo de ausencia si present=false
  /** Esfuerzo percibido reportado por el técnico para esta sesión (RPE 1-10). */
  rpe?: number;
}

export interface TechnificationSession extends Entity {
  programId: string;
  /** ISO date string 'YYYY-MM-DD' */
  date: string;
  coachId: string;
  location?: string;
  durationMinutes?: number;
  exercises: TechnificationExercise[];
  notes?: string;
  attendances: SessionAttendance[];
}

// ─── Evaluación ──────────────────────────────────────────────────────────────

export type EvaluationType = 'INITIAL' | 'MIDTERM' | 'FINAL' | 'EXTRA';

export interface TechnificationEvaluation extends Entity {
  programId: string;
  userId: string; // Participante evaluado
  coachId: string; // Técnico que evalúa
  /** ISO date string 'YYYY-MM-DD' */
  date: string;
  type: EvaluationType;

  /** Puntuaciones por clave de rúbrica (escala según `RubricCriterion.max`). */
  scores: Record<string, number | undefined>;

  /** Métricas físicas opcionales (medidas en sesión o importadas del wearable). */
  physicalMetrics?: {
    speed?: number; // m/s o km/h
    endurance?: number; // VO2Max estimado
    strength?: number;
    flexibility?: number;
  };

  observations?: string;
  privateNotes?: string; // Solo coaches/managers — NUNCA al jugador
  sharedWithPlayer: boolean;
}

// ─── Inscripción denormalizada ───────────────────────────────────────────────

export interface TechnificationEnrollment extends Entity {
  userId: string;
  programId: string;
  programName: string; // Denormalizado para listados rápidos en la app
  seasonId: string;
  /** ISO timestamp */
  enrolledAt: string;
  enrolledBy: string;
  active: boolean;
}

// ─── Objetivos individuales ──────────────────────────────────────────────────

export type GoalCategory =
  | 'TECHNIQUE'
  | 'TACTICS'
  | 'PHYSICAL'
  | 'MENTAL'
  | 'OTHER';

export type GoalStatus = 'PENDING' | 'IN_PROGRESS' | 'ACHIEVED' | 'DROPPED';

/**
 * Objetivo técnico/físico individual de un participante para la temporada.
 * Subcolección del programa. Visible para técnico y jugador.
 */
export interface TechnificationGoal extends Entity {
  programId: string;
  userId: string;
  createdBy: string; // coach/manager userId
  title: string;
  description?: string;
  category: GoalCategory;
  /** Métrica objetivo opcional, p. ej. "VO2Max ≥ 50". */
  targetMetric?: string;
  status: GoalStatus;
  /** Progreso 0-100, actualizado por el técnico. */
  progress: number;
  /** ISO date string 'YYYY-MM-DD' */
  dueDate?: string;
}

// ─── Autoevaluación / wellness (reportada por el deportista) ──────────────────

/**
 * Reporte diario de bienestar del deportista desde la app. Permite detectar
 * fatiga / riesgo de lesión y ajustar cargas. Escalas 1-5 (1=muy malo/alto dolor,
 * 5=óptimo) salvo `sessionRpe` (1-10).
 */
export interface TechnificationWellnessEntry extends Entity {
  userId: string;
  /** ISO date string 'YYYY-MM-DD' (un reporte por día y deportista). */
  date: string;
  sleepQuality: number; // 1-5
  fatigue: number; // 1-5 (5 = nada de fatiga)
  muscleSoreness: number; // 1-5 (5 = sin dolor)
  stress: number; // 1-5 (5 = sin estrés)
  mood: number; // 1-5
  /** RPE de la última sesión (escala de Borg modificada 1-10), opcional. */
  sessionRpe?: number;
  notes?: string;
}

/** Media de los 5 ejes de bienestar (1-5). Calculada en cliente. */
export function wellnessScore(e: TechnificationWellnessEntry): number {
  return (
    (e.sleepQuality + e.fatigue + e.muscleSoreness + e.stress + e.mood) / 5
  );
}

// ─── Resúmenes (devueltos por Cloud Function getTechnificationSummary) ────────

export interface ParticipantSummary {
  userId: string;
  totalSessions: number;
  attendedSessions: number;
  /** 0-100 */
  attendancePct: number;
  evaluationCount: number;
  /** Evolución de la media ponderada de scores por evaluación, en orden cronológico. */
  scoreTrend: { date: string; type: EvaluationType; average: number }[];
  lastEvaluationAverage?: number;
}

/** Media ponderada de una evaluación según la rúbrica del programa (0-`maxScale`). */
export function evaluationAverage(
  scores: Record<string, number | undefined>,
  rubric: RubricCriterion[] = DEFAULT_RUBRIC,
): number {
  let weighted = 0;
  let totalWeight = 0;
  for (const c of rubric) {
    const v = scores[c.key];
    if (typeof v === 'number') {
      const w = c.weight ?? 1;
      weighted += v * w;
      totalWeight += w;
    }
  }
  return totalWeight === 0 ? 0 : weighted / totalWeight;
}
